import format from 'date-fns/format'
import isValid from 'date-fns/isValid'
import parseISO from 'date-fns/parseISO'
import qs from 'query-string'
import { urlCreator } from '@pcln/flodash'
import { append, path, remove, times, update } from 'ramda'
import { NestDataObject } from 'react-hook-form'
import {
  FlightRecordType,
  FLIGHT_TYPE,
  FlightFormStateType,
  SetValueFunctionType,
  CABIN_CLASS_CODE_TYPE,
  FLIGHT_TYPE_CODE_TYPE
} from '../types'
import { LOCATION_SEARCH_TYPE } from '../../../types'
import analytics from '../../../utils/analytics'
import {
  ANALYTICS_CATEGORY_FLIGHTS,
  ANALYTICS_ACTION_FLIGHTS_SEARCH,
  INVALID
} from '../../../constants/analytics'

import {
  MULTI_DESTINATION,
  ONE_WAY,
  ROUND_TRIP,
  FLY,
  HOTEL,
  DRIVE,
  FLIGHT_TYPE_CODE,
  CABIN_CLASS
} from '../constants'
import { onSubmit as packagesOnSubmit } from '../../Packages/SearchForm/utils'
import {
  AIR_SEARCH_DATA,
  persistSearchData
} from '../../../utils/local-forage-helper'

const DATE_FORMAT_UI = 'MM/dd/yyyy'
const DATE_FORMAT_FORM_STATE = 'yyyyMMdd'

type FlightSearchRequestType = {
  flightTypeCode: FLIGHT_TYPE_CODE_TYPE | ''
  cabinClassCode: CABIN_CLASS_CODE_TYPE | ''
  adultCount: number
  youthCount?: number
  infantCount?: number
  childrenCount?: number
  flightDetails: FlightRecordType[]
}

type SlicedFlightInfoType = {
  origAirport: string
  destAirport: string
  origAirportType: string
  destAirportType: string
  departDate: string
}

type OptionParamsType = {
  'cabin-class': string
  'no-date-search': boolean
  'search-type': string
  'num-adults': number
  'num-youths': number
  'num-children': number
  'num-infants': number
  refclickid: string
}

type OptionsType = {
  slices: SlicedFlightInfoType[]
  totalPassengers: number
  params: Partial<OptionParamsType>
  mListings: boolean
  dealsUrl: boolean
  asUrl: boolean
  refclickid?: string
}

export function parseStringAndFormatAsDate(
  dateString: string,
  formatString: string
): string {
  const parsedDate = parseISO(dateString)
  if (isValid(parsedDate)) {
    return format(parsedDate, formatString)
  }
  return ''
}

function hasMultipleFlights(
  flightType: FLIGHT_TYPE | FLIGHT_TYPE_CODE_TYPE,
  flights: FlightRecordType[] | FlightRecordType
): boolean {
  return (
    Array.isArray(flights) &&
    flights.length > 0 &&
    (flightType === MULTI_DESTINATION ||
      flightType === FLIGHT_TYPE_CODE[MULTI_DESTINATION])
  )
}

function mapFormStateToSearchRequestType(
  formState: FlightFormStateType
): FlightSearchRequestType {
  const {
    flightType,
    cabinClass,
    flights,
    adultCount,
    youthCount,
    childrenCount,
    infantCount
  } = formState
  const flightTypeCode = flightType ? FLIGHT_TYPE_CODE[flightType] : ''
  const cabinClassCode = cabinClass ? CABIN_CLASS[cabinClass] : ''

  return {
    flightTypeCode,
    cabinClassCode,
    flightDetails: flights as FlightRecordType[],
    adultCount,
    youthCount,
    childrenCount,
    infantCount
  }
}

/**
 * Each slice corresponds to one leg of a journey i.e. from A -> B on a certain date
 * one-way will have only one slice (A -> B)
 * round-trip will have only two slices (A -> B and B -> A)
 * multi-destination can have more than two slices (A -> B, B -> C, C -> D etc)
 * @param origination
 * @param destination
 * @param travelDate
 */
function buildSlice(
  origination: LOCATION_SEARCH_TYPE,
  destination: LOCATION_SEARCH_TYPE,
  travelDate: string
): SlicedFlightInfoType {
  return {
    origAirport: origination.id,
    destAirport: destination.id,
    origAirportType: origination.lat && origination.lon ? '0' : '1',
    destAirportType: destination.lat && destination.lon ? '0' : '1',
    departDate: parseStringAndFormatAsDate(travelDate, DATE_FORMAT_UI)
  }
}

function buildSlices(
  formRequestState: FlightSearchRequestType
): SlicedFlightInfoType[] {
  const { flightTypeCode, flightDetails } = formRequestState
  switch (flightTypeCode) {
    case FLIGHT_TYPE_CODE[ONE_WAY]:
      if (!hasMultipleFlights(flightTypeCode, flightDetails)) {
        const { endLocation, startLocation, startDate } = flightDetails[0]
        return [buildSlice(startLocation, endLocation, startDate)]
      }

      break
    case FLIGHT_TYPE_CODE[ROUND_TRIP]:
      if (!hasMultipleFlights(flightTypeCode, flightDetails)) {
        const {
          endLocation,
          startLocation,
          startDate,
          endDate = ''
        } = flightDetails[0]
        return [
          buildSlice(startLocation, endLocation, startDate),
          buildSlice(endLocation, startLocation, endDate)
        ]
      }

      break
    case FLIGHT_TYPE_CODE[MULTI_DESTINATION]:
      if (hasMultipleFlights(flightTypeCode, flightDetails)) {
        return flightDetails.map(flightInfo => {
          const { endLocation, startLocation, startDate } = flightInfo
          return buildSlice(startLocation, endLocation, startDate)
        })
      }
      break
    default:
      break
  }
  return []
}

function buildRedirect(
  flightSearchRequestData: FlightSearchRequestType,
  sopq = false
): string {
  const {
    adultCount,
    childrenCount,
    cabinClassCode,
    youthCount,
    infantCount
  } = flightSearchRequestData
  const options: OptionsType = {
    slices: buildSlices(flightSearchRequestData),
    totalPassengers: adultCount + (childrenCount ?? 0),
    params: {
      'cabin-class': cabinClassCode,
      'no-date-search': false
    },
    mListings: !sopq,
    dealsUrl: sopq,
    asUrl: false
  }

  options.params['search-type'] = options.slices.reduce(
    (acc, { origAirportType, destAirportType }) =>
      `${acc}${origAirportType}${destAirportType}`,
    ''
  )
  options.params['num-adults'] = adultCount
  if (youthCount) {
    options.params['num-youths'] = youthCount
  }
  if (childrenCount) {
    options.params['num-children'] = childrenCount
  }
  if (infantCount) {
    options.params['num-infants'] = infantCount
  }
  const refClickId = path(
    ['PCLN_BOOTSTRAP_DATA', 'referral', 'clickId'],
    window
  ) as string | undefined
  if (refClickId) {
    options.params.refclickid = refClickId
    options.refclickid = refClickId
  }
  if (sopq) {
    // Takes a slice object and transforms it into a string
    // Ex. SFO-NYC-20201224
    const sliceStrings = options.slices.map(slice => {
      const origAirport = slice.origAirport.toUpperCase()
      const destAirport = slice.destAirport.toUpperCase()
      const formattedDate = format(
        new Date(slice.departDate),
        DATE_FORMAT_FORM_STATE
      )
      return `${origAirport}-${destAirport}-${formattedDate}`
    })
    const concatenatedSlices = sliceStrings.join('/')
    const dealsPath = `m/fly/search/${concatenatedSlices}/deals/`
    const queryParams = qs.stringify(options.params)
    const redirectUrl = `${dealsPath}?${queryParams}`
    return encodeURI(redirectUrl)
  }
  return urlCreator(options)
}

function searchRetail(formState: FlightFormStateType) {
  const flightSearchRequestData = mapFormStateToSearchRequestType(formState)
  const deepLinkUrl = buildRedirect(flightSearchRequestData)
  if (deepLinkUrl) {
    window.open(deepLinkUrl, '_blank')
  }
}

function searchSOPQ(formState: FlightFormStateType) {
  const isSOPQ = true
  const flightSearchRequestData = mapFormStateToSearchRequestType(formState)

  const deepLinkUrl = buildRedirect(flightSearchRequestData, isSOPQ)
  if (deepLinkUrl) {
    window.open(deepLinkUrl, '_blank')
  }
}

const getChildrenData = (
  youthCount: number,
  childrenCount: number,
  infantCount: number
) => {
  const childrenAges: number[] = []
  return childrenAges.concat(
    times(() => 0, infantCount),
    times(() => 14, childrenCount),
    times(() => 17, youthCount)
  )
}

export const onSubmit = (values: FlightFormStateType) => {
  analytics.fireEvent({
    category: values.gaCategory,
    action: ANALYTICS_ACTION_FLIGHTS_SEARCH,
    label: values.flightType
  })

  const flight = values.flights[0]
  if (
    (values.tripType.includes('STAY') || values.tripType.includes('DRIVE')) &&
    values.flightType === 'round-trip'
  ) {
    const childrenCount =
      values.youthCount + values.childrenCount + values.infantCount

    const childrenAges = getChildrenData(
      values.youthCount,
      values.childrenCount,
      values.infantCount
    )

    return packagesOnSubmit({
      adultCount: values.adultCount,
      childrenAges,
      childrenCount,
      endDate: flight.endDate,
      endLocation: flight.endLocation,
      partialStay: false,
      tripType: values.tripType,
      roomCount: values.roomCount,
      startDate: flight.startDate,
      startLocation: flight.startLocation,
      gaCategory: values.gaCategory
    })
  }

  persistSearchData(AIR_SEARCH_DATA, values)
  searchRetail(values)
}

export const onSubmitExpressDeal = (values: FlightFormStateType) => {
  analytics.fireEvent({
    category: values.gaCategory,
    action: ANALYTICS_ACTION_FLIGHTS_SEARCH,
    label: values.flightType
  })
  searchSOPQ(values)
}

export const setFlights = (
  flights: Partial<FlightRecordType>[],
  data: Partial<FlightRecordType>,
  setValue: SetValueFunctionType<FlightFormStateType>,
  index = 0
): void => {
  const flightToAdd = { ...flights[index], ...data }
  const result = update(index, flightToAdd, flights)
  setValue('flights', result)
}

export const addFlight = (
  flights: Partial<FlightRecordType>[],
  setValue: SetValueFunctionType<FlightFormStateType>
): void => {
  const { endLocation } = flights[flights.length - 1] || {}
  const nextFlight: Partial<FlightRecordType> = {
    startLocation: endLocation,
    endLocation: {} as LOCATION_SEARCH_TYPE
  }
  const result = append(nextFlight, flights)
  setValue('flights', result)
}

export const removeFlight = (
  flights: Partial<FlightRecordType>[],
  index: number,
  setValue: SetValueFunctionType<FlightFormStateType>
): void => {
  const result = remove(index, 1, flights)
  setValue('flights', result)
}

export const getPrevDate = (
  flights: Partial<FlightRecordType>[],
  index: number,
  dateFormat = DATE_FORMAT_FORM_STATE
) => {
  const defaultDate = parseStringAndFormatAsDate(
    new Date().toISOString(),
    dateFormat
  )
  if (index > 0) {
    const { startDate = '' } = flights[index - 1]
    return startDate
      ? parseStringAndFormatAsDate(startDate, dateFormat)
      : defaultDate
  }
  return defaultDate
}

export const initialState: FlightFormStateType = {
  flightType: ROUND_TRIP,
  flights: [
    {
      startLocation: {} as LOCATION_SEARCH_TYPE,
      endLocation: {} as LOCATION_SEARCH_TYPE,
      startDate: ''
    }
  ],
  tripType: FLY,
  cabinClass: 'Economy',
  roomCount: 1,
  adultCount: 1,
  youthCount: 0,
  infantCount: 0,
  childrenCount: 0,
  childrenAges: [],
  gaCategory: ANALYTICS_CATEGORY_FLIGHTS
}

export const fields = [
  'flights',
  'flightType',
  'roomCount',
  'adultCount',
  'childrenAges',
  'childrenCount',
  'infantCount',
  'youthCount',
  'tripType',
  'flightType',
  'cabinClass',
  'gaCategory'
]

export const isOneWay = (flightType: FLIGHT_TYPE) => flightType === ONE_WAY
export const isRoundTrip = (flightType: FLIGHT_TYPE) =>
  flightType === ROUND_TRIP
export const isMultiDestination = (flightType: FLIGHT_TYPE) =>
  flightType === MULTI_DESTINATION
export const includesStay = (tripType: string) => tripType.includes(HOTEL)
export const includesDrive = (tripType: string) => tripType.includes(DRIVE)
export const isPackage = (tripType: string) =>
  includesStay(tripType) || includesDrive(tripType)
export const showCabinClass = (flightType: FLIGHT_TYPE, tripType: string) =>
  !isPackage(tripType) || isOneWay(flightType) || isMultiDestination(flightType)

export const flightsStartLocationSearchKey = (index: number) =>
  `flights.${index}.startLocation`
export const flightsEndLocationSearchKey = (index: number) =>
  `flights.${index}.endLocation`
export const flightsDateRangeKey = (index: number) =>
  `flight-date-range-${index}`

export const flightSearchErrorStateAnalytics = (
  errors: NestDataObject<FlightFormStateType>
): void => {
  const errorKeys = Object.keys(errors)
  if (errorKeys.length > 0) {
    const errorProps = errorKeys.reduce(
      (errMsg, errorKey) => `${errMsg} | ${errorKey}`
    )
    const data = `${INVALID} - ${errorProps}`
    analytics.fireEvent({
      category: ANALYTICS_CATEGORY_FLIGHTS,
      action: ANALYTICS_ACTION_FLIGHTS_SEARCH,
      label: data
    })
  }
}
